/**
 * Route guard for the role-scoped Leads Workspace
 * (LEADS-WORKSPACE-ROLE-SCOPED-01). Mirrors `RequireNavSection`: the
 * pure decision lives in `decideLeadsWorkspaceAccess`
 * (leads-workspace-view.ts) and keys off the SERVER-computed
 * `me.leadsWorkspaceEnabled` signal — present only when the backend
 * flag is on AND the role is in the v1 audience (owner / coc /
 * receptionist). Absent (flag off, doctor, etc.) → redirect to the
 * caller's own homePath, so with the flag off nothing new is reachable
 * and no UI changes.
 *
 * Backend route auth (the workspace endpoint's role + clinic scope
 * gates) remains the real security boundary; this guard is UX
 * cleanliness only.
 */

import { Navigate, Outlet } from 'react-router-dom';
import { useDashboardMeContext } from '@/dashboard/me-context';
import { decideLeadsWorkspaceAccess } from '@/dashboard/leads/leads-workspace-view';
import type { DashboardMe } from '@/dashboard/types';

export interface RequireLeadsWorkspaceViewProps {
  readonly loading: boolean;
  readonly error: string | null;
  readonly me: DashboardMe | null;
  /** Rendered when access is granted. Defaults to `<Outlet />`. */
  readonly children?: React.ReactNode;
}

/** Pure presentational view — testable via react-dom/server. */
export function RequireLeadsWorkspaceView({
  loading,
  error,
  me,
  children,
}: RequireLeadsWorkspaceViewProps): JSX.Element | null {
  const decision = decideLeadsWorkspaceAccess({ loading, error, me });
  switch (decision.kind) {
    case 'wait':
      return null;
    case 'redirect':
      return <Navigate to={decision.to} replace />;
    case 'render':
      return <>{children ?? <Outlet />}</>;
    default: {
      const _exhaustive: never = decision;
      void _exhaustive;
      return null;
    }
  }
}

export default function RequireLeadsWorkspace(): JSX.Element | null {
  const ctx = useDashboardMeContext();
  if (!ctx) {
    return <Navigate to="/login" replace />;
  }
  return (
    <RequireLeadsWorkspaceView
      loading={ctx.loading}
      error={ctx.error}
      me={ctx.me}
    />
  );
}
